import { useState, useEffect, useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import { useDropzone } from 'react-dropzone'
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import Tooltip from '@mui/joy/Tooltip';
import LinearProgress from '@mui/joy/LinearProgress';
import AspectRatio from '@mui/joy/AspectRatio';
import IconButton from '@mui/joy/IconButton';

import useFile from 'hooks/useFile';

import useUserIdentity from 'hooks/useUserIdentity';
import { FileType, FileUploadTicketType } from 'stores/file_store';

import Button from '@mui/joy/Button';
import { getIcon } from 'utils/misc';
import { Success } from 'components/FancyToast';
import DateUI from 'components/Date';



function xhr_upload(url: string,
    file: File,
    onprogress: (event: ProgressEvent) => void,
    onloadend: (event: ProgressEvent) => void) {
    let xhr = new XMLHttpRequest();

    // track upload progress
    xhr.upload.onprogress = onprogress; // function(event) {
    //   console.log(`Uploaded ${event.loaded} of ${event.total}`);
    // };

    // track completion: both successful or not
    xhr.onloadend = onloadend; // function() {
        xhr.onerror = function () {
        console.log("error " + this.status);
    };

    xhr.open("PUT", url, true);
    xhr.setRequestHeader("Content-Type", file.type);

    xhr.send(file);
}


function FileUploadThumbFromCache({ file, onRemoveFile }: { file: FileType, onRemoveFile: (file: FileType) => void }) {

    const RemoveIcon = getIcon('Close');
    const [removing, setRemoving] = useState(false);

    return (
        <Sheet
            variant="soft"
            sx={{
                border: 1,
                padding: 1,
                borderColor: "divider",
                borderRadius: 3
            }}
        >
            <Stack
                justifyContent="space-between"
                spacing={1}
                direction="row">

                <Stack direction="row"
                    spacing={2}
                                    alignItems="flex-start">
                    <ThumbnailViewer file={file} />

                    <Stack>
                        <Tooltip arrow title={file.filename}>
                            <Typography
                                overflow="hidden"
                                level="body-sm"
                            >{file.filename}
                            </Typography>
                        </Tooltip>
                        <Typography
                            level="body-xs"
                        >Uploaded: <DateUI dateString={new Date(file.date_created).toString()} />
                        </Typography>
                    </Stack>
                </Stack>
                {!removing &&
                    <IconButton onClick={() => setRemoving(true)}>
                        <RemoveIcon />
                    </IconButton>
                }
                {removing &&
                    <Stack direction="row" spacing={1}>

                        <Button size="sm" variant="plain" onClick={() => setRemoving(false)}>
                            Cancel
                        </Button>
                        <Button size="sm" variant="outlined" color="danger" onClick={() => onRemoveFile(file)}>
                            Remove?
                        </Button>
                    </Stack>
                }

            </Stack>
        </Sheet>
    );

}

const ThumbnailViewer = observer(({file}: {file: FileType}) => {
    /* this will only work for the owner of the file */
    const AttachmentIcon = getIcon('Attachment');
    const { getFile, fetchFileByUuid, fetchThumbnailUrlByOwner } = useFile();
    const { currentIdentity } = useUserIdentity();
    // const [ourFile, setOurFile] = useState<FileType | null>(null);
    const [signedUrl, setSignedUrl] = useState<string | null>(null);

    // console.log("getfile:", getFile(file.uuid));
    const ourFile = getFile(file.uuid);
    useEffect(() => {
        if(!file.uuid) return
        const f = getFile(file.uuid);
        if(!f) {
            fetchFileByUuid(currentIdentity.uuid, file.uuid);
        }

    }, [file]);

    useEffect(() => {
        if(!signedUrl && ourFile?.thumb_ticket) {
            fetchThumbnailUrlByOwner(
                currentIdentity?.uuid,
                ourFile?.uuid
            ).then((s) => {
                s && setSignedUrl(s);
            });

        }
    }, [ourFile?.thumb_ticket]);

    // console.log("Viewer: ", ourFile, signedUrl);

    if(!signedUrl)
        return <AttachmentIcon fontSize="xl4" />;
    else  {
        return (
            <Stack>
            <AspectRatio ratio={1} sx={{width: 48, borderRadius: 'sm' }}>
            <img width={48}  src={signedUrl} />
            </AspectRatio>
            </Stack>
    );
    }
});

function FileUploadThumb({ file, onFileUploaded }: { file: File, onFileUploaded: (file: FileType) => void }) {

    const { getUploadTicket, createWithTicket } = useFile();
    const AttachmentIcon = getIcon('Attachment');

    const [started, setStarted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [ourFile, setOurFile] = useState<File | null>(null);


    const { currentIdentity } = useUserIdentity();
    const [progress, setProgress] = useState(0);
    const [ticket, setTicket] = useState('');

    const CheckIcon = getIcon('Check');

    useEffect(() => {
        console.log("useEffect: ", file);
        if(file && file == ourFile) {
            console.log("It's the same file!!!")
        }

        if (file && (file != ourFile)) {

            setOurFile(file);

            if (started) {
                return;
            }
            setStarted(true);

            getUploadTicket(currentIdentity.key)
                .then((resp: FileUploadTicketType | null) => {
                    if (!resp) {
                        Error("Unable to get upload ticket");
                        return;
                    }
                    setTicket(resp.ticket);
                    console.log("OK, got ticket: ", resp.ticket);
                    xhr_upload(resp.url,
                        file,
                        (event: ProgressEvent) => {
                            // console.log(`Uploaded ${event.loaded} of ${event.total}`);
                            const percent = Math.round((event.loaded / event.total) * 100);
                            setProgress(percent);
                        },
                        () => {
                            setSaving(true);
                            createWithTicket(currentIdentity.key, resp.ticket, file.name, file.type)
                                .then((file: FileType | null) => {
                                    if (!file) {
                                        Error("We had an error saving this file");
                                        return;
                                    }
                                    setSaving(false);
                                    setSaved(true);
                                    onFileUploaded(file);
                                    // console.log("File created with ticket", file);
                                })
                                .finally(() => {
                                        setProgress(0);

                            });

                        });

                });

            // console.log("file Ticket ready: ", ticket);
        }

    }, [file]);


    return (
        <Sheet
            variant="soft"
            sx={{
                border: 1,
                padding: 1,
                borderColor: "divider",
                borderRadius: 3
            }}
        >
            <Stack spacing={1}>
                <Stack direction="row"
                    spacing={2}
                    alignItems="flex-start">

                    <AttachmentIcon fontSize="xl4" />

                    <Stack alignItems="flex-start">
                        <Tooltip arrow title={file.name}>
                            <Typography
                                overflow="hidden"
                                level="body-sm"
                            >{file.name}
                            </Typography>
                        </Tooltip>
                        <Typography
                            level="body-xs"
                        >Last Updated: <DateUI dateString={new Date(file.lastModified).toString()} />
                        </Typography>

                        <Stack
                            direction="row"
                        >
                            {saving && <Typography
                                           level="body-xs">Saving</Typography>}
                            {saved && <Typography endDecorator={<CheckIcon />}
                                          level="body-xs">Uploaded</Typography>}
                            {!ticket && <Typography level="body-xs">Preparing</Typography>}

                        </Stack>
                    </Stack>
                </Stack>

                {started && progress > 0 &&

                    <LinearProgress value={progress} determinate={true} />

                }

            </Stack>
        </Sheet>

    );
}

export default function FileUpload({ setIsUploading, onFilesUploaded, uploadUuid, defaultFilesUploaded, children }: { setIsUploading: (isUploading: boolean) => void, onFilesUploaded: (files: FileType[]) => void, uploadUuid: number, defaultFilesUploaded?: FileType[] | undefined, children?: React.ReactNode}) {
    const [showing, setShowing] = useState(true);
    const [files, setFiles] = useState([] as File[]);
    const [filesUploaded, setFilesUploaded] = useState([] as FileType[]);
    const [uploadReady, setUploadReady] = useState(true);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        //        console.log("Accepted files: ", acceptedFiles);
        setFiles([...files, ...acceptedFiles]);
    }, [files]);

    const { getRootProps,
        getInputProps,
        isDragActive,
        isDragReject
    } = useDropzone({onDrop, multiple: false});

    useEffect(() => {
        // console.log("setIsUploading: ", !uploadReady);
        setIsUploading(!uploadReady);
    }, [uploadReady]);



    useEffect(() => {
        setFilesUploaded(defaultFilesUploaded ? defaultFilesUploaded : [] as FileType[]);
        setFiles([] as File[]);
    }, [uploadUuid, defaultFilesUploaded]);

    useEffect(() => {
        console.log("filesUploaded: ", filesUploaded);
        onFilesUploaded(filesUploaded);
    }, [filesUploaded.length]);


    useEffect(() => {
        // console.log('filesUploaded.length: ', filesUploaded.length);
        // console.log('files.length: ', files.length);
        if (filesUploaded.length > 0 && !uploadReady) {
            if (filesUploaded.length >= files.length) {
                Success("All files uploaded");
                setUploadReady(true);
                return;
            }
            // else {
            //     console.log("Not all files uploaded");
            //     console.log("filesUploaded: ", filesUploaded);
            //     console.log("files: ", files);
            // }
            // setUploadReady(false);
        }
        // console.log("Files uploaded: ", filesUploaded);
    }, [filesUploaded, files]);

    const DropIcon = getIcon('Download');
    const AttachIcon = getIcon('AttachFile');
    const CheckIcon = getIcon('CloudDoneOutlined');
    const UploadingIcon = getIcon('CloudUpload');

    const onFileUploaded = (file: FileType) => {
        // console.log("onFileUploaded: ", file);
        // console.log("filesUploaded: ", filesUploaded);
        // setTickets([...tickets, ticket]);
        // onFilesUploaded([...filesUploaded, file]);

        setFilesUploaded([...filesUploaded, file]);
        // console.log("file ready: ", file);
    };

    function onRemoveFile(file: FileType) {
        console.log("Removing file: ", file);
        console.log("filesUploaded: ", filesUploaded);
        console.log("filesUploaded.filter((f) => f.uuid != file.uuid): ", filesUploaded.filter((f) => f.uuid != file.uuid));
        setFilesUploaded(filesUploaded.filter((f) => f.uuid != file.uuid).slice());
    }


    if (!showing) {
        return (
            <Stack
                pr={1}
                pb={1}
                direction="row"
                justifyContent="flex-end">
                <Tooltip
                    arrow
                    title="Click or drop your files here to attach them">
                    <Typography
                        onClick={() => setShowing(true)}
                        startDecorator={<AttachIcon />}
                        level="body-xs">Attach files</Typography>
                </Tooltip>
            </Stack>
        );
    }


    return (
        <Stack width={"100%"} spacing={1}>

            {(filesUploaded.length > 0 || files.length > 0) &&
                <Stack spacing={0.5}>
                    { filesUploaded.map(f =>
                        <FileUploadThumbFromCache
                            onRemoveFile={onRemoveFile}
                            key={f.uuid}
                            file={f} />)  }

                    {files.map(file =>
                        <FileUploadThumb
                            key={file.name}
                            onFileUploaded={onFileUploaded}
                            file={file} />
                    )}
                </Stack>
            }

        <Sheet {...getRootProps()}

            variant={isDragActive ? "solid" : "outlined"}
            invertedColors={isDragActive}
            sx={{
                transition: "all 0.2s ease-in-out",
                p: isDragActive ? 5 : 0,
                mt: isDragActive ? 1 : 0,
                mb: 1,
                pb: 1,
                border: isDragActive ? 1 : 0,
                borderRadius: isDragActive ? 10 : 10,

                // borderTopLeftRadius: isDragActive ? 0 : 10,
            //     borderTopRightRadius: isDragActive ? 0 : 10,
                }}
                color={isDragReject ? 'danger' : 'primary'}
            >
                <input {...getInputProps()} />
                <Stack spacing={1}>
                    <Stack
                        pt={1}
                        direction="row"
                        justifyContent="center">
                        <Stack width="100%" >
                            <Stack width="100%" alignItems="center" >
                                {
                                    isDragActive ?
                                        <Typography
                                            startDecorator={<DropIcon />}
                                        >Drop the files here...</Typography>
                                        :
                                        <Typography
                                            sx={{ cursor: "default" }}
                                            startDecorator={<AttachIcon />}
                                            level="body-xs">Attach files</Typography>
                                }
                            </Stack>
                            <Stack sx={{ opacity: isDragActive ? 0.3 : 1 }} width="100%">
                                {children}
                            </Stack>
                        </Stack>

                    {files.length > 0 &&
                        <Stack
                            direction="row"
                            sx={{ position: "absolute", right: 2 }}
                            justifyContent="flex-end"
                            pr={1}>
                            {uploadReady && (files.length == filesUploaded.length) &&
                                <Typography
                                    startDecorator={<CheckIcon />}
                                    level="body-xs">All files uploaded</Typography>}

                            {!uploadReady &&
                                <Typography
                                    startDecorator={<UploadingIcon color="primary" />}
                                    level="body-xs">Uploading</Typography>}
                        </Stack>
                    }

                </Stack>
            </Stack>


        </Sheet>


        </Stack>

    );
}
